import { useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa6';
import { FiCheckCircle, FiRotateCcw } from 'react-icons/fi';
import { formatDate } from '../../../../utils/collateralFormatters';
import useLegalCaseMeasures, { type AddMeasureValue } from '../../../../hooks/useLegalCaseMeasures';
import useLegalCaseDeadlines, { type AddDeadlineValue } from '../../../../hooks/useLegalCaseDeadlines';
import {
    LEGAL_MEASURE_STATUS_OPTIONS,
    LegalMeasureStatus,
    type LegalDeadline,
    type LegalMeasure,
} from '../../../../types/LegalCase';
import LegalCaseMeasureForm from './LegalCaseMeasureForm';
import LegalCaseDeadlineForm from './LegalCaseDeadlineForm';
import ConfirmDialog from './ConfirmDialog';

interface LegalCaseMeasuresDeadlinesProps {
    caseId: string;
    initialMeasures?: LegalMeasure[];
    initialDeadlines?: LegalDeadline[];
}

// Cor do selo por status da medida. Pendentes em âmbar (é o que aparece na consulta "casos
// parados na citação"), deferida/cumprida em verde, indeferida/prescrita em cinza.
const measureStatusClass = (status: LegalMeasureStatus): string => {
    if (status === LegalMeasureStatus.GRANTED || status === LegalMeasureStatus.FULFILLED) {
        return 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300';
    }
    if (status === LegalMeasureStatus.REQUESTED || status === LegalMeasureStatus.PENDING) {
        return 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300';
    }
    return 'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-300';
};

// Uma linha de medida. Extraída para fora do componente (diretriz: sem função dentro de
// função). O dropdown de status muda a situação da medida (transição validada no backend).
const MeasureItem = ({
    measure,
    onChangeStatus,
    onRemove,
    disabled,
}: {
    measure: LegalMeasure;
    onChangeStatus: (status: LegalMeasureStatus) => void;
    onRemove: () => void;
    disabled: boolean;
}) => (
    <li className="flex items-start justify-between gap-2 py-2 border-b border-gray-100 dark:border-slate-700 last:border-0">
        <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{measure.measureTypeFormatted}</span>
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${measureStatusClass(measure.status)}`}>
                    {measure.statusFormatted}
                </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {measure.requestedAt && <span>Solicitada em {formatDate(measure.requestedAt)}</span>}
                {measure.resolvedAt && <span>{measure.requestedAt ? ' • ' : ''}Resolvida em {formatDate(measure.resolvedAt)}</span>}
            </div>
            {measure.description && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5 whitespace-pre-wrap break-words">{measure.description}</p>
            )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
            <select
                aria-label="Mudar status da medida"
                value={measure.status}
                disabled={disabled}
                onChange={(e) => onChangeStatus(e.target.value as LegalMeasureStatus)}
                className="appearance-none text-xs rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 pl-2 pr-6 py-1 disabled:opacity-50 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke%3D%22%236b7280%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%222%22%20d%3D%22M19%209l-7%207-7-7%22/%3E%3C/svg%3E')] bg-[length:0.85rem] bg-[right_0.35rem_center] bg-no-repeat"
            >
                {LEGAL_MEASURE_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
            <button
                type="button"
                onClick={onRemove}
                disabled={disabled}
                title="Remover medida"
                className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors disabled:opacity-50"
            >
                <FaTrash size={11} />
            </button>
        </div>
    </li>
);

// Uma linha de prazo. Prazo vencido e não cumprido fica destacado em vermelho (o `overdue`
// vem derivado do backend). Botão para marcar cumprido / reabrir.
const DeadlineItem = ({
    deadline,
    onToggleFulfilled,
    onRemove,
    disabled,
}: {
    deadline: LegalDeadline;
    onToggleFulfilled: () => void;
    onRemove: () => void;
    disabled: boolean;
}) => (
    <li className="flex items-start justify-between gap-2 py-2 border-b border-gray-100 dark:border-slate-700 last:border-0">
        <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{deadline.deadlineTypeFormatted}</span>
                <span className={`text-xs ${deadline.overdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                    {deadline.dueDate ? formatDate(deadline.dueDate) : '—'}
                </span>
                {deadline.overdue && (
                    <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300">
                        Vencido
                    </span>
                )}
                {deadline.fulfilled && (
                    <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
                        Cumprido
                    </span>
                )}
            </div>
            {deadline.description && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5 whitespace-pre-wrap break-words">{deadline.description}</p>
            )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
            <button
                type="button"
                onClick={onToggleFulfilled}
                disabled={disabled}
                title={deadline.fulfilled ? 'Reabrir prazo' : 'Marcar como cumprido'}
                className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
            >
                {deadline.fulfilled ? <FiRotateCcw size={13} /> : <FiCheckCircle size={13} />}
            </button>
            <button
                type="button"
                onClick={onRemove}
                disabled={disabled}
                title="Remover prazo"
                className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors disabled:opacity-50"
            >
                <FaTrash size={11} />
            </button>
        </div>
    </li>
);

// Seção "Medidas e prazos" do detalhe do caso. Reúne as medidas judiciais (SISBAJUD,
// RENAJUD, citação, etc., cada uma com um ciclo de status) e os prazos processuais (com
// vencimento). Substitui as colunas soltas da aba de RJ e o texto do RESUMO. Cada sub-lista
// tem seu formulário; a remoção passa por ConfirmDialog (padrão do módulo).
const LegalCaseMeasuresDeadlines = ({ caseId, initialMeasures, initialDeadlines }: LegalCaseMeasuresDeadlinesProps) => {
    const measures = useLegalCaseMeasures(caseId, initialMeasures);
    const deadlines = useLegalCaseDeadlines(caseId, initialDeadlines);

    const [showMeasureForm, setShowMeasureForm] = useState(false);
    const [showDeadlineForm, setShowDeadlineForm] = useState(false);
    const [pendingMeasureRemoval, setPendingMeasureRemoval] = useState<string | null>(null);
    const [pendingDeadlineRemoval, setPendingDeadlineRemoval] = useState<string | null>(null);

    const openMeasureForm = () => {
        measures.clearErrors();
        setShowMeasureForm(true);
    };

    const closeMeasureForm = () => {
        measures.clearErrors();
        setShowMeasureForm(false);
    };

    const openDeadlineForm = () => {
        deadlines.clearErrors();
        setShowDeadlineForm(true);
    };

    const closeDeadlineForm = () => {
        deadlines.clearErrors();
        setShowDeadlineForm(false);
    };

    const submitMeasure = async (value: AddMeasureValue): Promise<boolean> => {
        const ok = await measures.addMeasure(value);
        if (ok) closeMeasureForm();
        return ok;
    };

    const submitDeadline = async (value: AddDeadlineValue): Promise<boolean> => {
        const ok = await deadlines.addDeadline(value);
        if (ok) closeDeadlineForm();
        return ok;
    };

    const confirmMeasureRemoval = async () => {
        if (!pendingMeasureRemoval) return;
        await measures.removeMeasure(pendingMeasureRemoval);
        setPendingMeasureRemoval(null);
    };

    const confirmDeadlineRemoval = async () => {
        if (!pendingDeadlineRemoval) return;
        await deadlines.removeDeadline(pendingDeadlineRemoval);
        setPendingDeadlineRemoval(null);
    };

    return (
        <div className="space-y-6">
            {/* Medidas judiciais */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Medidas judiciais
                    </h4>
                    {!showMeasureForm && (
                        <button
                            type="button"
                            onClick={openMeasureForm}
                            className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                        >
                            <FaPlus size={10} /> Medida
                        </button>
                    )}
                </div>

                {measures.measures.length === 0 && !showMeasureForm && (
                    <p className="text-sm text-gray-400 dark:text-gray-500">Nenhuma medida registrada</p>
                )}

                {measures.measures.length > 0 && (
                    <ul>
                        {measures.measures.map((measure) => (
                            <MeasureItem
                                key={measure.id}
                                measure={measure}
                                onChangeStatus={(status) => measures.changeStatus(measure.id, status)}
                                onRemove={() => setPendingMeasureRemoval(measure.id)}
                                disabled={measures.isSaving}
                            />
                        ))}
                    </ul>
                )}

                {showMeasureForm && (
                    <LegalCaseMeasureForm
                        typeOptions={measures.typeOptions}
                        errorFields={measures.errorFields}
                        isSaving={measures.isSaving}
                        onSubmit={submitMeasure}
                        onCancel={closeMeasureForm}
                    />
                )}
            </div>

            {/* Prazos processuais */}
            <div className="space-y-3 border-t border-gray-200 dark:border-slate-700 pt-4">
                <div className="flex items-center justify-between">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Prazos processuais
                    </h4>
                    {!showDeadlineForm && (
                        <button
                            type="button"
                            onClick={openDeadlineForm}
                            className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                        >
                            <FaPlus size={10} /> Prazo
                        </button>
                    )}
                </div>

                {deadlines.deadlines.length === 0 && !showDeadlineForm && (
                    <p className="text-sm text-gray-400 dark:text-gray-500">Nenhum prazo registrado</p>
                )}

                {deadlines.deadlines.length > 0 && (
                    <ul>
                        {deadlines.deadlines.map((deadline) => (
                            <DeadlineItem
                                key={deadline.id}
                                deadline={deadline}
                                onToggleFulfilled={() => deadlines.setFulfilled(deadline.id, !deadline.fulfilled)}
                                onRemove={() => setPendingDeadlineRemoval(deadline.id)}
                                disabled={deadlines.isSaving}
                            />
                        ))}
                    </ul>
                )}

                {showDeadlineForm && (
                    <LegalCaseDeadlineForm
                        typeOptions={deadlines.typeOptions}
                        errorFields={deadlines.errorFields}
                        isSaving={deadlines.isSaving}
                        onSubmit={submitDeadline}
                        onCancel={closeDeadlineForm}
                    />
                )}
            </div>

            <ConfirmDialog
                open={pendingMeasureRemoval !== null}
                title="Remover medida"
                message="Remover esta medida do caso? Esta ação não pode ser desfeita."
                isBusy={measures.isSaving}
                onConfirm={confirmMeasureRemoval}
                onCancel={() => setPendingMeasureRemoval(null)}
            />

            <ConfirmDialog
                open={pendingDeadlineRemoval !== null}
                title="Remover prazo"
                message="Remover este prazo do caso? Esta ação não pode ser desfeita."
                isBusy={deadlines.isSaving}
                onConfirm={confirmDeadlineRemoval}
                onCancel={() => setPendingDeadlineRemoval(null)}
            />
        </div>
    );
};

export default LegalCaseMeasuresDeadlines;
